// /admin → Events Admin: every club, and everything they have put on the map.
//
// WHY IT IS ONE SCREEN AND NOT TWO. A club account and a club's events are the
// same object to the person moderating them — the question is never "is this
// login valid", it is "should this group be putting things on a campus map",
// and that is answered by looking at what they have made. Splitting the account
// into /admin → Accounts and the events into the Event Creator meant the two
// halves of one decision lived behind two different doors.
//
// ⚠️ APPROVAL IS THE POINT OF THIS SCREEN. Clubs register themselves now, so
// until somebody here approves one, nothing it publishes reaches the campus map.
// Everything else — sending a sign-in link, reading a club's events — is in
// service of that one call. The club can meanwhile sign in, fill in a profile
// and build events; it is a queue, not a wall, which is why an unapproved club
// with three finished events is a normal thing to see here and not an error.
//
// ⚠️ AND THERE ARE NO CLUB PASSWORDS AT ALL. A club signs in by following a
// one-use link mailed to its own address (api/club-setup.js), so a row offers
// "send a link" and there is nothing to reset, reveal or re-issue. That one
// button answers "we never got it", "we lost it" and "our exec graduated" —
// and a club can press it for itself from /eventmapcreator without asking.
import { useCallback, useEffect, useMemo, useState } from 'react';
// The same builder the club's own share screen uses, so "Open" lands on exactly
// the URL that went on their poster rather than a second one that happens to
// resolve — the id-vs-slug fallback is the whole reason that function exists.
import { eventUrl } from '../utils/eventSlug.js';
import { eventTags, eventWhere, ticketOf } from '../utils/eventTags.js';

const fmtDate = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString(); } catch { return ''; }
};

/** "2026-09-14" + "18:30" → what a poster would say. */
const fmtWhen = (e) => {
  if (!e?.date) return 'No date';
  const d = new Date(`${e.date}T${e.time || '00:00'}`);
  if (Number.isNaN(d.getTime())) return e.date;
  return d.toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(e.time ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
};

// Where an event is, in words — the same function every other screen uses, so
// the operator is reading exactly what an attendee reads. It was a local copy
// here until off-campus events arrived, at which point three copies of one rule
// became three chances for a moderation screen to describe an event differently
// from the map it is moderating.
const whereOf = eventWhere;

/**
 * The club's tags, as one line for a moderation queue.
 *
 * Text and not the badge row the public screens use: this file styles itself
 * with inline objects rather than a stylesheet, and a row here is scanned for
 * anything that needs a second look rather than read as a poster. Tickets are
 * on it deliberately — a club taking money through a link on a campus map is
 * the single thing on an event most worth an operator's eye, and the link is
 * one the operator should be able to open.
 */
function TagLine({ e }) {
  const parts = eventTags(e).map(t => t.label);
  const t = ticketOf(e);
  if (t) parts.push(t.label.replace(/^Buy tickets: /, 'Tickets '));
  if (!parts.length && !t) return null;
  return (
    <div style={S.meta}>
      {parts.join(' · ')}
      {t && <> · <a href={t.url} target="_blank" rel="noopener noreferrer">check the link</a></>}
    </div>
  );
}

/**
 * The three states, in the operator's words.
 *
 * Each says what it does to the two things that can be done with an event —
 * whether it is ADVERTISED and whether its LINK WORKS — because that is the
 * distinction the middle state exists for, and a switch labelled only
 * "unlisted" would leave the operator guessing whether the posters already on
 * walls still work. (They do. That is the point of it.)
 */
const STATUS = {
  listed: {
    label: 'Listed', said: 'listed',
    hint: 'On the map and in the app, and the link works.',
  },
  unlisted: {
    label: 'Unlisted', said: 'unlisted',
    hint: 'Off the map, the app and every listing — but the link and QR still work.',
  },
  disabled: {
    label: 'Disabled', said: 'disabled',
    hint: 'The link and the QR stop working. Nothing is deleted.',
  },
};
const STATUS_ORDER = ['listed', 'unlisted', 'disabled'];

/**
 * What an event's status is, including for records written before there was
 * one — the old `published: false` meant invisible everywhere, link included,
 * which is exactly `disabled`. Mirrors `eventStatus` in api/_eventStore.js.
 */
const statusOf = (e) => (STATUS[e?.status] ? e.status : (e?.published === false ? 'disabled' : 'listed'));

/**
 * The club an event is BY, as a key into `clubs`.
 *
 * ⚠️ NOT `e.club`. An event the operator made on a club's behalf is owned by
 * the operator's account, so `clubs[e.club]` is the UMaps profile — which is
 * how this list came to show the UMaps logo on events that were plainly a
 * club's. `host` is the name it is published under, so the club whose display
 * name (or short name) matches it wins. Mirrors `clubKeyForEvent` in
 * api/_eventStore.js, which the moderation endpoint uses.
 */
export function clubKeyOf(e, clubs) {
  const host = String(e?.host || '').trim().toLowerCase();
  if (host && clubs) {
    for (const [key, c] of Object.entries(clubs)) {
      if (String(c?.displayName || '').trim().toLowerCase() === host) return key;
    }
    for (const [key, c] of Object.entries(clubs)) {
      if (String(c?.shortName || '').trim().toLowerCase() === host) return key;
    }
  }
  return e?.club || '';
}

export default function EventsAdmin({ apiBase, token, onUnauthorized }) {
  const [data, setData] = useState(null);       // /api/accounts
  const [events, setEvents] = useState([]);     // /api/events?mine=1 (operator sees all)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');  // all | waiting | approved | setup
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(null);  // account id pending delete
  const [editing, setEditing] = useState(null);        // account id whose email is open
  const [killing, setKilling] = useState(null);        // event id pending delete
  const [open, setOpen] = useState({});                // club → events expanded
  const [view, setView] = useState('clubs');           // 'clubs' | 'events'
  // Links minted in this sitting, by account id. Kept in state on purpose: a
  // link is a credential, it is readable once, and it should die with the page.
  const [links, setLinks] = useState({});
  const [copied, setCopied] = useState('');

  const call = useCallback(async (path, method, body) => {
    const r = await fetch(`${apiBase}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 401 || r.status === 403) { onUnauthorized?.(); throw new Error('Not allowed — sign in as the admin.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'That did not go through.');
    return j;
  }, [apiBase, token, onUnauthorized]);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      // Two reads, deliberately in parallel: the accounts endpoint owns
      // approval and the events endpoint owns the events, and neither knows
      // about the other. Joining them here keeps that separation intact where
      // it matters (the public map reads only one of them).
      const [accts, evs] = await Promise.all([
        call('/api/accounts', 'GET'),
        call('/api/events?mine=1', 'GET').catch(() => ({ events: [] })),
      ]);
      setData(accts);
      setEvents(evs.events || []);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const act = async (body, okMsg) => {
    setBusy(true); setErr(''); setMsg('');
    let out = null;
    try { out = await call('/api/accounts', 'POST', body); setMsg(okMsg); await load(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
    return out;
  };

  // Events by club, so a row can carry its own without a second lookup per
  // render. Newest first — a moderator is looking at what just appeared.
  const eventsByClub = useMemo(() => {
    const by = {};
    for (const e of events) {
      if (!e?.club) continue;
      (by[e.club] ||= []).push(e);
    }
    for (const k of Object.keys(by)) {
      by[k].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    }
    return by;
  }, [events]);

  const clubs = useMemo(() => {
    const rows = (data?.accounts || [])
      .filter(a => a.tool === 'events')
      .map(a => ({
        ...a,
        club: data?.clubs?.[a.username] || null,
        events: eventsByClub[a.username] || [],
      }));
    const needle = q.trim().toLowerCase();
    const matched = rows.filter(a => {
      if (filter === 'unverified' && a.approved) return false;
      if (filter === 'approved' && !a.approved) return false;
      if (!needle) return true;
      return `${a.username} ${a.club?.displayName || ''} ${a.email || ''}`.toLowerCase().includes(needle);
    });
    // The queue first. Within it, a club that has actually FOLLOWED ITS LINK
    // outranks one that asked for one and never came back — both need
    // verifying, but only one has somebody waiting.
    //
    // ⚠️ `firstSignInAt`, not `pending`. Clubs have no password now (sign-in is
    // a link — see api/club-setup.js), so `pending` is true for every club on
    // the system and ranked them all identically.
    const rank = (a) => (a.approved ? 2 : a.firstSignInAt ? 1 : 0);
    return matched.sort((a, b) => rank(a) - rank(b)
      || (a.club?.displayName || a.username).localeCompare(b.club?.displayName || b.username));
  }, [data, eventsByClub, q, filter]);

  const all = (data?.accounts || []).filter(a => a.tool === 'events');
  const counts = {
    total: all.length,
    // ⚠️ ONE axis: verified or not. It used to be two — "waiting on you" and
    // "not set up" — which read as two separate queues when it is one list of
    // clubs nobody has looked at yet. Whether the club has been in yet is its
    // own business and shows on the row; whether we have verified it is the
    // whole job here.
    unverified: all.filter(a => !a.approved).length,
    approved: all.filter(a => a.approved).length,
  };
  // Events that exist and are reaching nobody. The honest headline for "what is
  // this queue costing" — an event held past its date is work thrown away.
  const heldEvents = events.filter(e =>
    e?.published !== false && !all.find(a => a.username === e.club)?.approved).length;

  // The op is still called `setup-link` on the wire — it is the same one-use
  // secret it always was — but what it buys is now a session rather than a
  // password form, so every word the operator reads says "sign-in".
  const setupLink = async (a, send) => {
    const out = await act(
      { op: 'setup-link', id: a.id, send },
      send ? `Sign-in link emailed to ${a.email}.` : `Sign-in link ready for ${a.club?.displayName || a.username}.`,
    );
    if (!out?.url) return;
    setLinks(prev => ({ ...prev, [a.id]: { url: out.url, sent: out.sent, to: out.to } }));
    if (send && !out.sent) {
      setMsg('');
      setErr(`${out.sendError || 'The email did not send.'} The link is below — send it yourself.`);
    }
  };

  const approve = async (a, next) => {
    const out = await act({ op: 'approve', id: a.id, approved: next },
      next ? `${a.club?.displayName || a.username} verified.` : `${a.club?.displayName || a.username} unverified.`);
    // Approving is a store change and the email is best-effort, so the screen
    // reports what actually happened rather than assuming the club was told.
    if (out && next && !out.emailed) {
      setMsg(`${a.club?.displayName || a.username} verified — but we could not email them${out.emailError ? ` (${out.emailError})` : ''}. Let them know yourself.`);
    }
  };

  const killEvent = async (e) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      // The operator's own token, against the same endpoint a club uses. The
      // ownership check in _eventStore.js lets an admin through — one rule,
      // one place — rather than this screen having a delete of its own.
      await call('/api/events', 'POST', { deleteId: e.id });
      setMsg(`Deleted “${e.name || 'that event'}”.`);
      setConfirming(null); setKilling(null);
      await load();
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  };

  /**
   * Move an event between listed / unlisted / disabled.
   *
   * Optimistic on the row, then reloaded: this is a three-way switch somebody
   * clicks along a list, and a full round trip before the button moves makes it
   * feel like it did not take.
   */
  const setEventStatus = async (e, status) => {
    setBusy(true); setErr(''); setMsg('');
    setEvents(list => list.map(x => (x.id === e.id ? { ...x, status } : x)));
    try {
      await call('/api/events', 'POST', { status: { id: e.id, status } });
      setMsg(`“${e.name || 'That event'}” is now ${STATUS[status].said}.`);
      await load();
    } catch (e2) {
      setErr(e2.message);
      await load();          // put the row back to whatever the store actually says
    }
    setBusy(false);
  };

  const copy = async (text, key) => {
    try { await navigator.clipboard?.writeText(text); } catch { /* on screen either way */ }
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? '' : c)), 1800);
  };

  return (
    <div>
      {/* Two views of the same data, because there are two jobs. Verifying is
          about a CLUB and reads down a list of them; moderating is about an
          EVENT and reads in date order across every club — hunting one event
          through sixty collapsed club rows is the wrong shape for "what is
          going on the map this week". */}
      <div style={S.viewTabs}>
        <button style={{ ...S.viewTab, ...(view === 'clubs' ? S.viewTabOn : {}) }}
                onClick={() => setView('clubs')}>Clubs ({counts.total})</button>
        <button style={{ ...S.viewTab, ...(view === 'events' ? S.viewTabOn : {}) }}
                onClick={() => setView('events')}>Events ({events.length})</button>
      </div>

      {view === 'events' ? (
        <EventsList events={events} accounts={all} clubs={data?.clubs || {}}
                    busy={busy} err={err} msg={msg}
                    killing={killing} setKilling={setKilling} onKill={killEvent}
                    onStatus={setEventStatus} onRefresh={load} />
      ) : (
      <>
      <div style={S.counts}>
        <Stat label="Clubs" value={counts.total} />
        <Stat label="Unverified" value={counts.unverified} tone={counts.unverified ? 'warn' : undefined}
              hint="nothing they post reaches the map" />
        <Stat label="Verified" value={counts.approved} />
        <Stat label="Events held" value={heldEvents} tone={heldEvents ? 'warn' : undefined}
              hint="published by a club you have not verified" />
      </div>

      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      <div style={S.toolbar}>
        {[['all', `All (${counts.total})`],
          ['unverified', `Unverified (${counts.unverified})`],
          ['approved', `Verified (${counts.approved})`]].map(([k, label]) => (
          <button key={k} style={{ ...S.chip, ...(filter === k ? S.chipOn : {}) }}
                  onClick={() => setFilter(k)}>{label}</button>
        ))}
        <input style={S.search} value={q} onChange={e => setQ(e.target.value)}
               placeholder="Find a club by name or email" />
        <button style={S.ghost} onClick={() => { setAdding(a => !a); setErr(''); setMsg(''); }}>
          {adding ? 'Cancel' : '+ Add a club'}
        </button>
        <button style={S.ghost} onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
      </div>

      {adding && (
        <AddClub busy={busy}
                 onCancel={() => setAdding(false)}
                 onCreate={async (body) => {
                   const out = await act({ op: 'create', tool: 'events', pending: true, ...body },
                     `${body.displayName || body.username} created.`);
                   if (out?.account) setAdding(false);
                 }} />
      )}

      {clubs.map(a => {
        const name = a.club?.displayName || a.username;
        const live = a.events.filter(e => e?.published !== false);
        const isOpen = !!open[a.username];
        return (
          <div key={a.id} style={S.card}>
            <div style={S.rowTop}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.name}>
                  {a.club?.logo
                    ? <img src={a.club.logo} alt="" style={S.logo} />
                    : <span style={S.logoNone} aria-hidden="true">🎪</span>}
                  <span style={{ minWidth: 0 }}>{name}</span>
                  {/* Three states, one badge. Approved is the quiet one — it is
                      the state most rows will be in once the queue is worked
                      through, and a green flag on fifty rows stops meaning
                      anything. */}
                  {a.approved
                    ? <span style={S.badgeOk}>Verified</span>
                    : <span style={S.badgeHold}>Unverified</span>}
                  {/* Separate from the badge, because it is a different fact:
                      whether the CLUB has been in yet. A club can be verified
                      and never have followed its link, or unverified having
                      built five events.

                      ⚠️ NOT `pending`, which is "has no password" and is now
                      true of every club forever — it said "no password yet" on
                      rows belonging to clubs that had been publishing for
                      months. `firstSignInAt` is the honest version of the
                      question the badge was always asking. */}
                  {!a.firstSignInAt && <span style={S.badgeSetup}>never signed in</span>}
                  {/* Made from the Event Creator's Host picker, not by the club.
                      Until somebody there follows a sign-in link it is emailed
                      nothing at all (`clubMailable` in api/_accounts.js), so the
                      row says so — "verified" and silent is otherwise a puzzle. */}
                  {a.createdBy === 'umaps' && (
                    <span style={S.badgeUmaps}
                          title={a.firstSignInAt ? undefined : 'Not emailed about its events until someone at the club signs in with a link'}>
                      {a.firstSignInAt ? 'Created by UMaps' : 'Created by UMaps · not emailed'}
                    </span>
                  )}
                </div>
                <div style={S.meta}>
                  {a.email
                    ? <span style={S.email}>{a.email}</span>
                    : <span style={S.blocked}>no email — nowhere to send a link</span>}
                  {' '}
                  {/* The way out of the commonest dead end on this screen. 53 of
                      the 60 placeholder clubs ship with no published address, so
                      "no email" is the normal starting state and not an error —
                      it just needs somewhere to type one. */}
                  <button style={S.inlineEdit}
                          title={a.email
                            ? 'Change the address this club signs in with'
                            : 'Add an address so this club can be sent a sign-in link'}
                          onClick={() => { setEditing(editing === a.id ? null : a.id); setErr(''); }}>
                    {editing === a.id ? 'cancel' : a.email ? 'change' : 'add one'}
                  </button>
                  {' · '}
                  <code style={S.code}>{a.username}</code>
                  {!a.club?.hasLogo && <span style={S.todo}> · no logo yet</span>}
                  {a.setupLink && <span style={S.todo}> · link outstanding</span>}
                  {a.createdBy === 'umaps' ? ' · added by UMaps ' : ' · joined '}{fmtDate(a.createdAt)}
                  {a.approvedAt ? ` · approved ${fmtDate(a.approvedAt)}` : ''}
                </div>
                {editing === a.id && (
                  <EmailEditor
                    account={a} busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={async (value) => {
                      const out = await act({ op: 'update', id: a.id, email: value },
                        value
                          ? `${name} signs in with ${value}.`
                          : `${name} has no sign-in address — nobody can get in.`);
                      if (out) {
                        setEditing(null);
                        // Said out loud, because it is a second thing that
                        // happened: a club with no public contact cannot publish
                        // at all, and this quietly unblocked that too.
                        if (out.profileEmail) {
                          setMsg(`${name} signs in with ${value} — and it is now their public contact too, which they needed before they could publish.`);
                        }
                      }
                    }} />
                )}

                {links[a.id] && (
                  <div style={S.linkBox}>
                    <div style={S.linkNote}>
                      {links[a.id].sent ? `Emailed to ${links[a.id].to}. ` : 'Not emailed — send it yourself. '}
                      Works once, until it is used or replaced.
                    </div>
                    <div style={S.linkRow}>
                      <code style={S.linkUrl}>{links[a.id].url}</code>
                      <button style={S.ghost} onClick={() => copy(links[a.id].url, a.id)}>
                        {copied === a.id ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {confirming === a.id ? (
                <div style={S.rowActions}>
                  <span style={S.confirmText}>Delete {name} and its {a.events.length} event{a.events.length === 1 ? '' : 's'}?</span>
                  <button style={S.ghost} onClick={() => setConfirming(null)} disabled={busy}>Keep</button>
                  <button style={S.danger} disabled={busy}
                          onClick={() => act({ op: 'delete', id: a.id }, `Deleted ${name}.`).then(() => setConfirming(null))}>
                    Delete
                  </button>
                </div>
              ) : (
                <div style={S.rowActions}>
                  {/* The one call this screen exists for, so it is the primary
                      button and it says what it does to the public map rather
                      than naming a flag. */}
                  {a.approved ? (
                    <button style={S.ghost} disabled={busy}
                            title="Take this club off the public map. Nothing is deleted — its events stop being published."
                            onClick={() => approve(a, false)}>Unverify</button>
                  ) : (
                    <button style={S.primary} disabled={busy}
                            title="Publish this club's events, and email them to say so"
                            onClick={() => approve(a, true)}>Verify</button>
                  )}
                  <button style={S.ghost} disabled={busy || !a.email}
                          title={a.email ? `Email a one-use link to ${a.email}` : 'No email on file for this club'}
                          onClick={() => setupLink(a, true)}>
                    {a.firstSignInAt ? 'Send a new link' : 'Send sign-in link'}
                  </button>
                  <button style={S.ghost} disabled={busy}
                          title="Get the link without sending it"
                          onClick={() => setupLink(a, false)}>Copy link</button>
                  <button style={S.dangerGhost} onClick={() => { setConfirming(a.id); setErr(''); }}>Delete</button>
                </div>
              )}
            </div>

            {/* The club's events, folded away. A moderator scanning sixty clubs
                wants the list of clubs; the events are what they open when one
                row raises a question. The COUNT is always visible, because "has
                this club actually done anything" is part of the approve call. */}
            <button style={S.disclose} onClick={() => setOpen(o => ({ ...o, [a.username]: !o[a.username] }))}>
              <span style={S.discloseArrow}>{isOpen ? '▾' : '▸'}</span>
              {a.events.length === 0
                ? 'No events yet'
                : `${a.events.length} event${a.events.length === 1 ? '' : 's'}`}
              {live.length > 0 && !a.approved && (
                <span style={S.todo}> · {live.length} published, none reaching the map</span>
              )}
            </button>

            {isOpen && a.events.length > 0 && (
              <div style={S.events}>
                {a.events.map(e => (
                  <div key={e.id} style={S.event}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.eventName}>{e.name || 'Untitled event'}</div>
                      <div style={S.meta}>
                        {fmtWhen(e)}
                        {whereOf(e) ? ` · ${whereOf(e)}` : ''}
                        {/* Three things can keep an event off the map and they
                            are not the same: the club unpublished it, we have
                            not approved the club, or it is simply in the past.
                            Saying which stops "why isn't it showing" being a
                            guess. */}
                        {e.published === false
                          ? <span style={S.todo}> · unpublished by the club</span>
                          : a.approved
                            ? <span style={S.liveTag}> · live</span>
                            : <span style={S.todo}> · held, club not verified</span>}
                      </div>
                    </div>
                    {killing === e.id ? (
                      <div style={S.rowActions}>
                        <span style={S.confirmText}>Delete this event?</span>
                        <button style={S.ghost} onClick={() => setKilling(null)} disabled={busy}>Keep</button>
                        <button style={S.danger} disabled={busy} onClick={() => killEvent(e)}>Delete</button>
                      </div>
                    ) : (
                      <button style={S.dangerGhost} onClick={() => { setKilling(e.id); setErr(''); }}>
                        Take down
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {clubs.length === 0 && !busy && (
        <div style={S.empty}>
          {q || filter !== 'all' ? 'No club matches that.' : 'No clubs yet. They appear here as soon as one registers at /eventmapcreator.'}
        </div>
      )}
      </>
      )}
    </div>
  );
}

/**
 * Every event on the map, in date order — the moderation view.
 *
 * Sorted SOONEST FIRST among things that have not happened, with the past
 * folded away behind a count. What an operator is looking at this screen for is
 * "what is about to go up on the map", and a list that opens on last October's
 * bake sale buries it.
 *
 * Each row says why an event is or is not public, because the reasons are
 * different and need different actions: we set it to something other than
 * listed (this row), the club is not verified (yours, one click, on the Clubs
 * tab), or it is simply in the past (nobody's).
 */
function EventsList({ events, accounts, clubs, busy, err, msg, killing, setKilling,
                      onKill, onStatus, onRefresh }) {
  const [q, setQ] = useState('');
  const [showPast, setShowPast] = useState(false);
  const approvedOf = useMemo(
    () => new Map(accounts.map(a => [a.username, a.approved])), [accounts]);

  const startOf = (e) => {
    if (!e?.date) return null;
    const d = new Date(`${e.date}T${e.time || '00:00'}`);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const now = Date.now();

  const { upcoming, past } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = events
      .map(e => ({
        ...e,
        clubName: clubs[clubKeyOf(e, clubs)]?.displayName || e.host || e.club,
        // The same mark the Clubs tab draws, from the same `clubs` map. Fifty
        // rows of one date-ordered list belonging to twenty clubs is a wall of
        // text otherwise: the logo is how "three of these in a row are UMSU" is
        // read without reading. It is what an attendee sees on the map pin too,
        // which is the point of an operator's glance down this list.
        clubLogo: clubs[clubKeyOf(e, clubs)]?.logo || '',
        verified: approvedOf.get(e.club) === true,
        start: startOf(e),
      }))
      .filter(e => !needle
        // `whereOf` and not the building name, so an off-campus event answers
        // to its address — it has no building to be found by.
        || `${e.name} ${e.clubName} ${whereOf(e)}`.toLowerCase().includes(needle));
    // No date at all sorts with the upcoming: it is unfinished work, not history.
    const up = rows.filter(e => !e.start || e.start.getTime() >= now)
      .sort((a, b) => (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity));
    const old = rows.filter(e => e.start && e.start.getTime() < now)
      .sort((a, b) => b.start.getTime() - a.start.getTime());
    return { upcoming: up, past: old };
  }, [events, clubs, approvedOf, q, now]);

  const held = upcoming.filter(e => statusOf(e) === 'listed' && !e.verified).length;
  const offMap = upcoming.filter(e => statusOf(e) !== 'listed').length;

  const row = (e) => {
    const status = statusOf(e);
    return (
    <div key={e.id} style={S.event}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.eventName}>{e.name || 'Untitled event'}</div>
        <div style={{ ...S.meta, ...S.eventMeta }}>
          {/* Same drawing and same fallback as a club row above — a club with no
              logo yet is a to-do, not a blank. `alt=""` because the name is
              right beside it: read out, the mark would be the club's name
              twice. */}
          {e.clubLogo
            ? <img src={e.clubLogo} alt="" style={S.logoSm} />
            : <span style={S.logoSmNone} aria-hidden="true">🎪</span>}
          <b>{e.clubName}</b>
          {' · '}{fmtWhen(e)}
          {whereOf(e) ? ` · ${whereOf(e)}` : ''}
        </div>
        <TagLine e={e} />
        {/* ⚠️ WHAT IS TRUE RIGHT NOW, not what the switch says. The switch is
            our decision; this line is its consequence once the club's own
            verification and the calendar have had their say — an event set to
            "listed" for a club nobody has verified is still not on the map, and
            saying "live" there would be a lie the operator acts on. */}
        <div style={S.meta}>
          {status === 'disabled'
            ? <span style={S.todo}>disabled — the link and QR do not work</span>
            : !e.verified
              ? <span style={S.todo}>held — club not verified</span>
              : status === 'unlisted'
                ? <span style={S.dim}>unlisted — reachable by link only</span>
                : e.start && e.start.getTime() < now
                  ? <span style={S.dim}>past</span>
                  : <span style={S.liveTag}>live on the map</span>}
        </div>
      </div>
      {killing === e.id ? (
        <div style={S.rowActions}>
          <span style={S.confirmText}>Delete this event?</span>
          <button style={S.ghost} onClick={() => setKilling(null)} disabled={busy}>Keep</button>
          <button style={S.danger} disabled={busy} onClick={() => onKill(e)}>Delete</button>
        </div>
      ) : (
        <div style={S.rowActions}>
          {/* Three states as one switch, not three buttons: they are mutually
              exclusive and the one that is on is the fact worth reading. It sits
              beside "Open", because checking what an attendee sees is the thing
              you do immediately before and after moving it. */}
          <div style={S.seg} role="group" aria-label="Event status">
            {STATUS_ORDER.map((k, i) => (
              <button key={k} type="button" title={STATUS[k].hint}
                      aria-pressed={status === k} disabled={busy || status === k}
                      style={{
                        ...S.segBtn,
                        ...(i === STATUS_ORDER.length - 1 ? { borderRight: 'none' } : {}),
                        ...(status === k ? S.segOn[k] : {}),
                      }}
                      onClick={() => onStatus(e, k)}>
                {STATUS[k].label}
              </button>
            ))}
          </div>
          {/* What an attendee sees. The fastest way to tell whether an event is
              actually finished is to open the page it points at. */}
          <a style={S.ghost} href={eventUrl(e, undefined, events)}
             target="_blank" rel="noreferrer">Open</a>
          <button style={S.dangerGhost} onClick={() => setKilling(e.id)}>Delete</button>
        </div>
      )}
    </div>
    );
  };

  return (
    <div>
      <div style={S.counts}>
        <Stat label="Upcoming" value={upcoming.length} />
        <Stat label="Held" value={held} tone={held ? 'warn' : undefined}
              hint="listed, but the club is not verified" />
        {/* Something we took off the map is something we have to remember to put
            back. Without a count it is invisible until a club writes in. */}
        <Stat label="Not listed" value={offMap}
              hint="unlisted or disabled by us" />
        <Stat label="Past" value={past.length} />
      </div>

      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      <div style={S.toolbar}>
        <input style={S.search} value={q} onChange={e => setQ(e.target.value)}
               placeholder="Find an event by name, club or building" />
        <button style={S.ghost} onClick={onRefresh} disabled={busy}>
          {busy ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div style={S.card}>
        {upcoming.map(row)}
        {upcoming.length === 0 && (
          <div style={S.empty}>{q ? 'No event matches that.' : 'Nothing coming up.'}</div>
        )}
      </div>

      {past.length > 0 && (
        <>
          <button style={S.disclose} onClick={() => setShowPast(v => !v)}>
            <span style={S.discloseArrow}>{showPast ? '▾' : '▸'}</span>
            {past.length} past event{past.length === 1 ? '' : 's'}
          </button>
          {showPast && <div style={S.card}>{past.map(row)}</div>}
        </>
      )}
    </div>
  );
}

/**
 * The address a club signs in with.
 *
 * Two jobs behind one field, and they carry different risk, so the wording
 * changes rather than the form:
 *
 *  · FILLING IN A BLANK. The normal case — a seeded club has no published
 *    address, so it cannot be sent a sign-in link and is stuck. Nothing is at
 *    stake; there is no login to break.
 *  · MOVING AN EXISTING ONE. A real need (a club's inbox changes hands between
 *    execs) and a real hazard: it is the credential. Whoever reads the new
 *    address can take the club over with one link, and whoever held the old one
 *    is locked out. So it says so, plainly, above the button.
 *
 * Clearing it is allowed and is how you revoke a club's way back in — but it is
 * not a way to pause one, so the form says that instead of leaving it to be
 * discovered.
 */
function EmailEditor({ account, busy, onCancel, onSave }) {
  const [value, setValue] = useState(account.email || '');
  const v = value.trim().toLowerCase();
  const ok = v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  const changed = v !== (account.email || '');
  const moving = !!account.email && v !== '' && changed;
  return (
    <div style={S.emailForm}>
      <div style={S.formRow}>
        <input style={S.input} autoFocus type="email" value={value}
               placeholder="club@umanitoba.ca"
               onChange={e => setValue(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter' && ok && changed) onSave(v); }} />
        <button style={S.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={S.primary} disabled={!ok || !changed || busy} onClick={() => onSave(v)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      {!ok && <div style={{ ...S.formHint, color: '#c5221f' }}>That is not an email address.</div>}
      {ok && moving && (
        <div style={{ ...S.formHint, color: '#a15c00' }}>
          <b>This address IS the club's login.</b> Moving it means whoever reads {v} can
          take the club over with one link, and whoever used {account.email} can no longer
          sign in at all — there is no password to fall back on. Send them a new link
          afterwards.
        </div>
      )}
      {ok && v === '' && account.email && (
        <div style={{ ...S.formHint, color: '#c5221f' }}>
          Clearing it means nobody can sign in as this club and no link can be sent. That
          is how you revoke one — it is not a way to pause one.
        </div>
      )}
      {ok && !moving && v !== '' && !account.email && (
        <div style={S.formHint}>
          Where every sign-in link goes — this is how the club gets in, so it has to be
          an inbox they still read. It also becomes the club's public contact if it has
          none, which it needs before it can publish anything.
        </div>
      )}
    </div>
  );
}

/**
 * Adding a club by hand — the operator doing what a club would otherwise do for
 * itself, for the one that phoned instead of using the form.
 *
 * It still creates a HELD account: the club gets the same sign-in link and gets
 * in the same way. An operator-made shortcut that handed over a ready-made
 * login would be a second way in to maintain, and one that skipped approval
 * would quietly defeat the point of the screen.
 */
function AddClub({ busy, onCancel, onCreate }) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const ok = displayName.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  // Same derivation the registration endpoint uses, shown so the operator can
  // see what the club's internal key will be rather than discovering it later.
  const slug = displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 32) || 'club';
  return (
    <div style={S.form}>
      <div style={S.formRow}>
        <input style={S.input} autoFocus placeholder="Club name"
               value={displayName} onChange={e => setDisplayName(e.target.value)} />
        <input style={S.input} type="email" placeholder="Club email — where the sign-in link goes"
               value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      <div style={S.formHint}>
        Creates a held account, exactly as if the club had registered itself. Send it a
        sign-in link and it is in — there is no password either way. Login name will be{' '}
        <code style={S.code}>{slug}</code>.
      </div>
      <div style={S.rowActions}>
        <button style={S.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={S.primary} disabled={!ok || busy}
                onClick={() => onCreate({
                  username: slug,
                  displayName: displayName.trim(),
                  email: email.trim().toLowerCase(),
                  note: displayName.trim(),
                })}>
          {busy ? 'Creating…' : 'Create club'}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div style={{ ...S.stat, ...(tone === 'warn' ? S.statWarn : {}) }}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  );
}

const S = {
  viewTabs: { display: 'flex', gap: 6, marginBottom: 14 },
  viewTab: { background: '#fff', color: '#3c4043', border: '1.5px solid #dadce0', borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  viewTabOn: { background: '#202124', color: '#fff', borderColor: '#202124' },
  counts: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  stat: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '11px 14px', minWidth: 118 },
  statWarn: { background: '#fffaf0', boxShadow: '0 2px 12px rgba(160,110,0,0.14)' },
  statValue: { fontSize: 22, fontWeight: 800, color: '#202124', lineHeight: 1.1 },
  statLabel: { fontSize: 12, fontWeight: 700, color: '#3c4043', marginTop: 2 },
  statHint: { fontSize: 10.5, color: '#80868b', marginTop: 2, maxWidth: 150, lineHeight: 1.35 },

  toolbar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 14px' },
  chip: { background: '#fff', color: '#3c4043', border: '1.5px solid #dadce0', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  chipOn: { background: '#1a73e8', color: '#fff', borderColor: '#1a73e8' },
  search: { flex: '1 1 200px', minWidth: 0, fontSize: 13.5, padding: '8px 10px', border: '1.5px solid #dadce0', borderRadius: 8, outline: 'none' },

  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 10, overflow: 'hidden' },
  rowTop: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 14px', flexWrap: 'wrap' },
  name: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 800, color: '#202124', minWidth: 0 },
  logo: { width: 22, height: 22, borderRadius: 5, objectFit: 'contain', flexShrink: 0, background: '#f1f3f4' },
  logoNone: { width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, background: '#f1f3f4', borderRadius: 5, flexShrink: 0 },
  // The same mark on an events row, scaled to the 11.5px line it sits on: at 22
  // it was taller than the two lines of text beside it and pushed the row open.
  logoSm: { width: 16, height: 16, borderRadius: 4, objectFit: 'contain', flexShrink: 0, background: '#f1f3f4' },
  logoSmNone: { width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, background: '#f1f3f4', borderRadius: 4, flexShrink: 0 },
  meta: { fontSize: 11.5, color: '#5f6368', marginTop: 3 },
  // The club line is the only meta line carrying a picture, so it is the only
  // one that has to lay itself out as a row rather than as text.
  eventMeta: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  email: { color: '#1a56c4', fontWeight: 600 },
  code: { background: '#f1f3f4', borderRadius: 4, padding: '1px 5px', fontSize: 11 },
  blocked: { color: '#c5221f', fontWeight: 700 },
  todo: { color: '#a15c00', fontWeight: 600 },
  liveTag: { color: '#137333', fontWeight: 700 },

  badgeOk: { background: '#e6f4ea', color: '#137333', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, flexShrink: 0 },
  badgeHold: { background: '#fff3e0', color: '#a15c00', border: '1px solid #f3d9a8', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, flexShrink: 0 },
  badgeUmaps: { background: '#e8f0fe', color: '#1a73e8', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, flexShrink: 0 },
  badgeSetup: { background: '#f1f3f4', color: '#5f6368', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, flexShrink: 0 },

  rowActions: { display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' },
  // One switch, three positions. A shared border and no gaps, so the three read
  // as settings of one thing rather than as three buttons that might each do
  // something on their own.
  seg: { display: 'flex', border: '1.5px solid #dadce0', borderRadius: 8, overflow: 'hidden' },
  segBtn: { background: '#fff', color: '#5f6368', border: 'none', borderRight: '1.5px solid #dadce0', padding: '7px 11px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  // The colour says what the state DOES, not which position it is in: green is
  // on the map, grey is off it but still reachable, red is the link switched
  // off. The one that is on is filled, so a glance down the column reads.
  segOn: {
    listed: { background: '#e6f4ea', color: '#137333', cursor: 'default' },
    unlisted: { background: '#eceff1', color: '#3c4043', cursor: 'default' },
    disabled: { background: '#fce8e6', color: '#c5221f', cursor: 'default' },
  },
  confirmText: { fontSize: 12.5, color: '#202124', fontWeight: 600 },
  primary: { background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  ghost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  danger: { background: '#c5221f', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  dangerGhost: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3c2c0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },

  disclose: { display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', background: '#fafbfc', border: 'none', borderTop: '1px solid #f1f3f4', padding: '9px 14px', fontSize: 12.5, fontWeight: 600, color: '#5f6368', cursor: 'pointer', font: 'inherit' },
  discloseArrow: { fontSize: 10, opacity: 0.7 },
  events: { borderTop: '1px solid #f1f3f4' },
  event: { display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px 10px 26px', borderBottom: '1px solid #f6f7f8', flexWrap: 'wrap' },
  eventName: { fontSize: 13.5, fontWeight: 700, color: '#202124' },

  form: { background: '#f8fbff', border: '1px solid #d7e6fb', borderRadius: 12, padding: '13px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  // Inside a row rather than above the list, so it stays attached to the club
  // it is about while sixty of them are on screen.
  emailForm: { marginTop: 8, background: '#f8fbff', border: '1px solid #d7e6fb', borderRadius: 8, padding: '10px 11px', display: 'flex', flexDirection: 'column', gap: 7 },
  // A verb in the metadata line, not a button in the actions: it belongs to the
  // address it sits beside, and the row's real buttons are the ones that act on
  // the club.
  inlineEdit: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 11.5, fontWeight: 700, color: '#1a73e8', textDecoration: 'underline', cursor: 'pointer' },
  formRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  formHint: { fontSize: 11.5, color: '#5f6368', lineHeight: 1.45 },
  input: { flex: '1 1 200px', minWidth: 0, fontSize: 13.5, padding: '8px 10px', border: '1.5px solid #dadce0', borderRadius: 8, outline: 'none' },

  linkBox: { marginTop: 8, background: '#fffaf0', border: '1px solid #f5d9a8', borderRadius: 8, padding: '8px 10px' },
  linkNote: { fontSize: 11.5, color: '#8a6414', marginBottom: 6, lineHeight: 1.45 },
  linkRow: { display: 'flex', gap: 8, alignItems: 'center' },
  linkUrl: { flex: 1, minWidth: 0, overflowX: 'auto', whiteSpace: 'nowrap', background: '#fff', border: '1px solid #eadcc0', borderRadius: 6, padding: '6px 8px', fontSize: 11.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },

  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
  empty: { padding: '18px 4px', fontSize: 13, color: '#5f6368' },
  dim: { color: '#80868b' },
};
